<script setup lang="ts">
/**
 * 指派校验任务弹窗。文档/视频：workspace_ready；教案：口播已确认（含 notes_ready / completed）。
 * 教案目的语由服务端按被指派人语种计算，弹窗无目的语下拉，202 即关闭不等 MT Job。
 * 选人：optgroup 分「本组 / metalingual组」，组内展示「姓名-擅长语种」；空语种/同源语/无法映射置灰。
 */
import { computed, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'

import { ProjectType } from '@/api/common-types'
import type { UserSummary } from '@/api/common-types'
import {
  getTaskSelectionOptions,
  type TaskSelectionOptions,
} from '@/modules/task/api/task'
import {
  assignReviewTask as projectAssignReviewTask,
  getWorkItem,
} from '@/modules/project/api/work-item'
import { assignReviewTask as teachingAssignReviewTask } from '@/modules/teaching/api/teaching'

const LANG_CODE_RE = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})?$/

const props = defineProps<{
  workItemId: number
  itemType?: string
}>()

const emit = defineEmits<{
  assigned: [taskId: number, jobId?: number | null]
}>()

const { t } = useI18n()
const visible = defineModel<boolean>('visible', { default: false })
const loading = ref(false)
const loadingUsers = ref(false)
const options = ref<TaskSelectionOptions | null>(null)
const selectedUserId = ref<number>()
const fileName = ref('')
const sourceLanguage = ref('')
const resolvedItemType = ref(props.itemType || '')

const isLessonItem = computed(
  () =>
    props.itemType === ProjectType.LESSON ||
    resolvedItemType.value === ProjectType.LESSON,
)

function normalizeLang(raw?: string | null): string {
  return (raw || '').trim().toLowerCase().split('-')[0] || ''
}

function isUnmappableLanguage(raw?: string | null): boolean {
  const text = (raw || '').trim()
  if (!text) return true
  return !LANG_CODE_RE.test(text)
}

function isAssigneeDisabled(user: UserSummary): boolean {
  const raw = user.language?.trim() || ''
  if (!raw || isUnmappableLanguage(raw)) return true
  const code = normalizeLang(raw)
  const source = normalizeLang(sourceLanguage.value)
  return Boolean(source) && code === source
}

/** 组内选项文案：姓名-擅长语种（无语种则省略） */
function formatAssigneeLabel(user: UserSummary): string {
  const name = (user.name || user.username || String(user.id)).trim()
  const lang = user.language?.trim()
  return lang ? `${name}-${lang}` : name
}

function toLeafOptions(users: UserSummary[]) {
  return users.map((u) => ({
    value: u.id,
    label: formatAssigneeLabel(u),
    disabled: isAssigneeDisabled(u),
  }))
}

const selectOptions = computed(() => {
  if (!options.value) return []
  const groups: Array<{ label: string; options: ReturnType<typeof toLeafOptions> }> = []
  const same =
    options.value.can_assign_to_group !== false
      ? options.value.same_group_users || []
      : []
  const meta =
    options.value.can_assign_to_metalingual !== false
      ? options.value.metalingual_users || []
      : []
  if (same.length > 0) {
    groups.push({
      label: t('project.actionBar.assigneeGroupSame'),
      options: toLeafOptions(same),
    })
  }
  if (meta.length > 0) {
    groups.push({
      label: t('project.actionBar.assigneeGroupMetalingual'),
      options: toLeafOptions(meta),
    })
  }
  return groups
})

const hasAnyAssignee = computed(() =>
  selectOptions.value.some((g) => g.options.length > 0),
)

watch(
  visible,
  async (open) => {
    if (!open) return
    selectedUserId.value = undefined
    loadingUsers.value = true
    try {
      const [wi, opts] = await Promise.all([
        getWorkItem(props.workItemId),
        getTaskSelectionOptions(),
      ])
      resolvedItemType.value = String(wi.item_type || props.itemType || '')
      sourceLanguage.value = String(wi.source_language || '')
      const lesson = resolvedItemType.value === ProjectType.LESSON
      const stage = String(wi.pipeline_stage || '')
      const status = String(wi.status || '')
      const ready = lesson
        ? stage === 'notes_ready' ||
          stage === 'completed' ||
          status === 'completed' ||
          stage === 'machine_running' ||
          stage === 'awaiting_review' ||
          stage === 'in_review'
        : stage === 'workspace_ready'
      if (!ready) {
        message.error(t('project.actionBar.assignNotReady'))
        visible.value = false
        return
      }
      fileName.value = wi.file_name || ''
      options.value = opts
    } catch (e) {
      console.error(e)
      visible.value = false
    } finally {
      loadingUsers.value = false
    }
  },
  { immediate: true },
)

async function confirm() {
  if (!selectedUserId.value) {
    message.warning(t('project.actionBar.selectAssignee'))
    return
  }
  const selected = selectOptions.value
    .flatMap((g) => g.options)
    .find((opt) => opt.value === selectedUserId.value)
  if (selected?.disabled) {
    message.warning(t('project.actionBar.selectAssignee'))
    return
  }
  loading.value = true
  try {
    const body = { assignee_id: selectedUserId.value }
    if (isLessonItem.value) {
      const result = await teachingAssignReviewTask(props.workItemId, body)
      emit('assigned', Number(result.task_id || 0), result.job_id ?? null)
    } else {
      const result = await projectAssignReviewTask(props.workItemId, body)
      emit('assigned', result.task_id)
    }
    visible.value = false
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <a-modal
    v-model:open="visible"
    :title="t('project.actionBar.assignReview')"
    :confirm-loading="loading"
    :ok-button-props="{ disabled: loadingUsers || !hasAnyAssignee }"
    @ok="confirm"
  >
    <p v-if="fileName" class="mb-2">{{ fileName }}</p>
    <a-spin :spinning="loadingUsers">
      <a-empty
        v-if="!loadingUsers && !hasAnyAssignee"
        :description="t('project.actionBar.noAssignees')"
      />
      <a-select
        v-else
        v-model:value="selectedUserId"
        style="width: 100%"
        :placeholder="t('project.actionBar.selectAssignee')"
        show-search
        option-filter-prop="label"
        :options="selectOptions"
      />
    </a-spin>
  </a-modal>
</template>
